import BaseCardBehavior from './BaseCardBehavior';
import CardStateHelper, { getCardsState } from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { SCORE_BOOM_CARD, SCORE_BOOM_TARGET_CARD } from '../../../Constants/Score';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { CardModifierType } from '../../../Prototype/Level/Card/CardModifierType';
import { IGameState } from '../../Game/IGameState';
import { ICardStateData } from '../ICardState';
import { initialCardStateData } from '../initialCardStateData';
import { CardState } from 'Prototype/Level/Card/CardState';
import { ArrayHelper } from 'Utility/ArrayHelper';
import CardStateModifierHelper from '../CardStateModifierHelper';

export default class BoomCardBehavior extends BaseCardBehavior {
    public play(state: IGameState): IGameState {
        const currentCardState = CardStateHelper.getCardState(state, this.cardID);

        let targets: string[] = [];

        if (currentCardState === CardState.IN_PLAY) {
            const boomCardData = CardStateHelper.getCardStateData(state, this.cardID);
            const offsetBoom = boomCardData.offset ?? initialCardStateData.offset.clone();
            const removableCardIDs = CardStateHelper.getCardsInPlay(state.levelState.cardsState, {
                requireCardTypes: [CardType.value],
            });

            removableCardIDs.forEach((cardID: string) => {
                if (cardID === this.cardID) return; // Skip this card.

                const cardData = CardStateHelper.getCardStateData(state, cardID);
                const offsetCard = cardData.offset ?? initialCardStateData.offset.clone();

                if (
                    cardData.type === CardType.gold ||
                    cardData.type === CardType.scissors ||
                    cardData.type === CardType.key ||
                    this.hasInvalidModifier(cardData)
                )
                    return;

                const dx = Math.abs(boomCardData.x + offsetBoom.x - cardData.x + offsetCard.x);
                const dy = Math.abs(boomCardData.y + offsetBoom.y - cardData.y + offsetCard.y);
                if (dx < 300 && dy < 360) {
                    const behavior = CardStateHelper.getCardBehavior(state.levelState.cardsState, cardID);
                    if (
                        !behavior.isBlocked(state.levelState.cardsState) &&
                        behavior.getCardType(state.levelState.cardsState) !== CardType.lock
                    ) {
                        // Collect affected card IDs for task data.
                        targets.push(cardID);

                        const connectedModifiers = CardStateHelper.getModifiersOfType(
                            state.levelState.cardsState,
                            cardID,
                            CardModifierType.connected
                        );
                        connectedModifiers.forEach(connectedModifier => {
                            if (connectedModifier.properties.cardID) {
                                targets.push(connectedModifier.properties.cardID);
                            }
                        });
                    }
                }
            });
        }

        // Discard self.
        state = CardStateHelper.setCardDiscarded(state, this.cardID);
        targets = targets.filter(ArrayHelper.filterOnce);
        state = CardStateHelper.setCardDiscarded(state, ...targets);

        let cardsState = getCardsState(state);

        // Add score.
        const scores: number[] = [SCORE_BOOM_CARD, ...Array(targets.length).fill(SCORE_BOOM_TARGET_CARD)];
        cardsState = CardStateHelper.addTurnScore(cardsState, ...scores);

        // Trigger explosion task.
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.BOOM,
            cards: [this.cardID, ...targets],
            cardsScore: [...scores],
        });

        // Update all cards behavior after some of them are discarded.
        state = CardStateHelper.updateGameState(state, cardsState);
        state = CardStateHelper.updateCards(state);
        state = CardStateModifierHelper.onCardPlayed(state, this.cardID);
        cardsState = getCardsState(state);

        // Clear the undo history when a boom card is played.
        cardsState = CardStateHelper.clearUndoHistory(cardsState);

        return {
            ...state,
            levelState: { ...state.levelState, cardsState },
        };
    }

    public hasInvalidModifier(cardData: ICardStateData): boolean {
        return cardData.modifiers.some(modifier => {
            return modifier.type === CardModifierType.rope;
        });
    }
    public hasInvalidType(cardData: ICardStateData): boolean {
        switch (cardData.type) {
            case CardType.key:
            case CardType.gold:
            case CardType.scissors:
            case CardType.boom:
                return false;
            default:
                return true;
        }
    }
}
